import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Coins, Wallet, Landmark } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fmtMilli } from "../../lib/mshopCalc";

const PERIODS = [["daily", "اليوم"], ["weekly", "الأسبوع"], ["monthly", "الشهر"], ["yearly", "السنة"]];
const omanToday = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Muscat" });

export function BalanceStat({ icon: Icon, label, value, unit, testId }) {
    return (
        <div className="rounded-3xl bg-white p-5 shadow-sm ring-1 ring-[#E7E5DF]" data-testid={testId}>
            <div className="flex items-center gap-2 text-sm font-semibold text-neutral-600"><Icon className="h-4 w-4" />{label}</div>
            {value == null ? <div className="mt-2 text-lg font-bold text-amber-700">غير محدد</div> :
                <div className="mt-2 flex items-baseline gap-1.5"><span dir="ltr" className="font-numbers text-2xl font-black text-neutral-900">{fmtMilli(value)}</span><span className="text-xs text-neutral-500">{unit}</span></div>}
        </div>
    );
}

export default function ShopLogView({ employeeId }) {
    const [period, setPeriod] = useState("daily");
    const [ref, setRef] = useState(omanToday());
    const [data, setData] = useState(null);
    useEffect(() => {
        let alive = true;
        setData(null);
        api.get("/mshop/shop-log", { params: { period, date: ref, employee_id: employeeId } })
            .then((r) => { if (alive) setData(r.data); })
            .catch((e) => { if (alive) { toast.error(formatApiError(e.response?.data?.detail)); setData(false); } });
        return () => { alive = false; };
    }, [period, ref, employeeId]);
    return (
        <div dir="rtl" data-testid="mshop-shop-log">
            <div className="mb-4 flex flex-wrap items-center gap-2">
                <div className="flex flex-1 gap-1 rounded-2xl bg-white p-1.5 ring-1 ring-[#E7E5DF]">
                    {PERIODS.map(([k, l]) => <button key={k} onClick={() => setPeriod(k)} className={`flex-1 rounded-xl py-2 text-sm font-bold ${period === k ? "bg-[#1F1D1A] text-[#F3DFA2]" : "text-neutral-600 hover:bg-neutral-50"}`}>{l}</button>)}
                </div>
                <input type="date" value={ref} onChange={(e) => e.target.value && setRef(e.target.value)} className="h-12 rounded-2xl border border-[#DAD7CF] bg-white px-3 font-numbers text-sm" />
            </div>
            {data === null ? <div className="py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-neutral-400" /></div> : data === false ? null : <>
                <div className="mb-3 text-xs text-neutral-500">الفترة: {data.start} → {data.end} • {data.flows.sales_count} بيع • {data.flows.purchases_count} شراء • {data.flows.expenses_count} مصاريف</div>
                {!data.opening.set && <div className="mb-3 rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-900">يرجى تحديد الرصيد الابتدائي من الإعدادات.</div>}
                <div className="mb-3 grid grid-cols-2 gap-3">
                    <div className="rounded-3xl bg-white p-4 shadow-sm ring-1 ring-[#E7E5DF]" data-testid="mshop-stat-bought">
                        <div className="text-center text-lg font-bold text-[#35D94A]">شراء جرام</div>
                        <div className="mt-1 text-center font-numbers text-3xl font-black text-[#35D94A]" dir="ltr">{data.current.bought_weight == null ? "—" : fmtMilli(data.current.bought_weight)}</div>
                        <div className="mt-1 text-center text-xs text-neutral-500">إجمالي أوزان الشراء • جم</div>
                    </div>
                    <div className="rounded-3xl bg-white p-4 shadow-sm ring-1 ring-[#E7E5DF]" data-testid="mshop-stat-sold">
                        <div className="text-center text-lg font-bold text-[#F06443]">بيع جرام</div>
                        <div className="mt-1 text-center font-numbers text-3xl font-black text-[#F06443]" dir="ltr">{data.current.sold_weight == null ? "—" : fmtMilli(data.current.sold_weight)}</div>
                        <div className="mt-1 text-center text-xs text-neutral-500">إجمالي أوزان البيع • جم</div>
                    </div>
                </div>
                <div className="grid grid-cols-1 gap-3">
                    <BalanceStat icon={Wallet} label="الكاش الحالي" value={data.current.cash} unit="ر.ع" testId="mshop-stat-current-cash" />
                    <BalanceStat icon={Landmark} label="البنك الحالي" value={data.current.bank} unit="ر.ع" testId="mshop-stat-current-bank" />
                </div>
            </>}
        </div>
    );
}
